// src/modules/app-views/invoices/components/CardHeaderInvoiceSubtitle.jsx
// *******************************************************************************
// INVOICE CARD HEADER SUBTITLE
// *******************************************************************************
// Displays recipient CNPJ/CPF and location (City - UF) for existing invoices.
// Shows "single_invoice" label for new documents.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { parseCnpjToDisplay, parseCpfToDisplay, isCpfOrCnpj } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CardHeaderInvoiceSubtitle = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const currentPage = useZustand?.('module', 'currentPage', false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const data = currentPage?.data;
  const isNew = !data?._id || data?._id === 'add';

  // For new documents, show simple label
  if (isNew) {
    return <span>{translateX('single_invoice')}</span>;
  }

  // Build recipient document label (CNPJ or CPF)
  // recipientEntity has the full entity data (cnpj, cpf, etc.)
  // recipient has basic address data
  const recipientEntity = data?.recipientEntity || {};
  const recipient = data?.recipient || {};

  // Get document from recipientEntity first (has full data)
  const cpfCnpj =
    recipientEntity?.cnpj?.value ||
    recipientEntity?.cpf?.value ||
    recipientEntity?.cpfCnpj ||
    recipient?.cnpj?.value ||
    recipient?.cpf?.value ||
    '';

  // Detect document type and format
  const documentType = isCpfOrCnpj(cpfCnpj);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  let docLabel;
  if (documentType === 'cnpj') {
    docLabel = `CNPJ: ${parseCnpjToDisplay(cpfCnpj)}`;
  } else if (documentType === 'cpf') {
    docLabel = `CPF: ${parseCpfToDisplay(cpfCnpj)}`;
  } else {
    docLabel = 'CPF: N/A';
  }

  // Build location (City - UF) - prefer recipient.address (has city directly)
  const city = recipient?.address?.city || recipientEntity?.address?.city;
  const uf = recipient?.address?.region || recipientEntity?.address?.region;
  const location = city && uf ? `${city} - ${uf}` : city || uf || '';

  // Format: "CNPJ: XX.XXX.XXX/XXXX-XX | City - UF"
  const subtitle = location ? `${docLabel} | ${location}` : docLabel;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return <span>{subtitle}</span>;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderInvoiceSubtitle;

// *******************************************************************************
// HELPERS
// *******************************************************************************
